import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirmPassword) return toast.error('Passwords do not match');

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setResult('success');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reset failed. The link may have expired.';
      toast.error(msg);
      setResult('error');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="reset-password-page">
        <Navbar />
        <div className="h-20" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Invalid reset link</h1>
            <Link to="/forgot-password">
              <Button variant="outline" data-testid="request-new-link-btn">Request a new link</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="reset-password-page">
      <Navbar />
      <div className="h-20" />
      <Toaster />

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {result === 'success' ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-semibold mb-2">Password reset!</h1>
              <p className="text-gray-500 mb-6">Your password has been updated. You can now log in.</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#D65A31] hover:bg-[#B84A28] text-white"
                data-testid="go-to-login-btn"
              >
                Go to login
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold mb-2 text-center">Set a new password</h1>
              <p className="text-gray-500 text-center mb-8">Enter your new password below.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">New password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Confirm password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D65A31] hover:bg-[#B84A28] text-white py-5"
                  data-testid="reset-password-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reset password
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
